/**
 * Раздел «Предупреждения»: адрес и отбор — чистая часть страницы.
 *
 * Ключи адреса: `date` — день (пусто = сегодня), `kinds` — роды через запятую
 * (пусто = все), `msg` — выбранное сообщение «PAC-2026-3058», плюс общие
 * ключи рубежей `eez`, `fd`.
 */
import type { NavwarnCollection, NavwarnMessage } from '../api'
import { NAVWARN_KINDS, presentKinds } from '../map/navwarn'

export interface MsgKey { navarea: string; year: number; number: number }

export interface NavwarnUrlState {
  date: string | null
  kinds: string[]
  msg: MsgKey | null
  eez: boolean
  districts: boolean
}

export const NAVWARN_URL_KEYS = ['date', 'kinds', 'msg', 'eez', 'fd'] as const

const DAY = /^\d{4}-\d{2}-\d{2}$/
const MSG = /^(PAC|XII|ARC)-(\d{4})-(\d{1,5})$/

export const msgKeyString = (k: MsgKey): string => `${k.navarea}-${k.year}-${k.number}`

export const sameMsg = (a: MsgKey | null, b: MsgKey | null): boolean =>
  !!a && !!b && a.navarea === b.navarea && a.year === b.year && a.number === b.number

export function parseNavwarnUrl(p: URLSearchParams): NavwarnUrlState {
  const date = p.get('date')
  const m = MSG.exec(p.get('msg') ?? '')
  const known = new Set<string>(NAVWARN_KINDS)
  return {
    // Ссылка приходит извне: битая дата читается как «сегодня», а не роняет запрос.
    date: date && DAY.test(date) ? date : null,
    kinds: (p.get('kinds') ?? '').split(',').filter(k => known.has(k)),
    msg: m ? { navarea: m[1], year: Number(m[2]), number: Number(m[3]) } : null,
    eez: p.get('eez') === '1',
    districts: p.get('fd') === '1',
  }
}

export function writeNavwarnUrl(s: NavwarnUrlState): URLSearchParams {
  const out = new URLSearchParams()
  if (s.date) out.set('date', s.date)
  if (s.kinds.length) out.set('kinds', s.kinds.join(','))
  if (s.msg) out.set('msg', msgKeyString(s.msg))
  if (s.eez) out.set('eez', '1')
  if (s.districts) out.set('fd', '1')
  return out
}

/** Пустой отбор = все роды. Незнакомый род отбирается как «прочее». */
export function kindPasses(kind: string, kinds: string[]): boolean {
  if (!kinds.length) return true
  return kinds.includes((NAVWARN_KINDS as readonly string[]).includes(kind) ? kind : 'other')
}

/** Ответ, урезанный до выбранных родов: и фигуры, и список. */
export function filterCollection(fc: NavwarnCollection, kinds: string[]): NavwarnCollection {
  if (!kinds.length) return fc
  return {
    ...fc,
    features: fc.features.filter(f => kindPasses(f.properties.kind, kinds)),
    messages: fc.messages.filter(m => kindPasses(m.kind, kinds)),
  }
}

/** Фасеты родов кадра: род → сколько сообщений. Порядок — легенды. */
export function kindFacets(messages: NavwarnMessage[]): Array<{ kind: string; count: number }> {
  const counts = new Map<string, number>()
  for (const k of presentKinds(messages.map(m => m.kind))) counts.set(k, 0)
  for (const m of messages) {
    const k = (NAVWARN_KINDS as readonly string[]).includes(m.kind) ? m.kind : 'other'
    counts.set(k, (counts.get(k) ?? 0) + 1)
  }
  return [...counts].map(([kind, count]) => ({ kind, count }))
}

/** Рамка всех фигур сообщения — чтобы подлететь к нему. Долгота знаковая. */
export function messageBounds(
  fc: NavwarnCollection, id: number,
): [[number, number], [number, number]] | null {
  let w = Infinity, s = Infinity, e = -Infinity, n = -Infinity
  const visit = (c: unknown): void => {
    if (Array.isArray(c) && typeof c[0] === 'number') {
      const [x, y] = c as number[]
      w = Math.min(w, x); e = Math.max(e, x); s = Math.min(s, y); n = Math.max(n, y)
    } else if (Array.isArray(c)) c.forEach(visit)
  }
  for (const f of fc.features) {
    if (f.properties.id === id && f.geometry && 'coordinates' in f.geometry) visit(f.geometry.coordinates)
  }
  if (!Number.isFinite(w)) return null
  // Фигура у шва лежит двумя половинами (−180…−179 и 179…180): рамка через
  // весь мир увела бы карту прочь — сводим в восточную шкалу.
  if (e - w > 180) {
    let w2 = Infinity, e2 = -Infinity
    const east = (c: unknown): void => {
      if (Array.isArray(c) && typeof c[0] === 'number') {
        const x = (c[0] as number) < 0 ? (c[0] as number) + 360 : (c[0] as number)
        w2 = Math.min(w2, x); e2 = Math.max(e2, x)
      } else if (Array.isArray(c)) c.forEach(east)
    }
    for (const f of fc.features) {
      if (f.properties.id === id && f.geometry && 'coordinates' in f.geometry) east(f.geometry.coordinates)
    }
    w = w2; e = e2
  }
  return [[w, s], [e, n]]
}

const SMALL_WORDS = new Set(['of', 'the', 'and', 'off', 'in', 'to', 'de', 'la'])

/** «SEA OF JAPAN.» → «Sea of Japan»: сводка набрана капслоком, а в списке
 *  капслок кричит громче рода предупреждения. Текст оригинала не трогается. */
export function regionLabel(raw: string): string {
  return raw
    .trim()
    .replace(/\.$/, '')
    .toLowerCase()
    .split(/(\s+|-)/)
    .map((w, i) => (i > 0 && SMALL_WORDS.has(w) ? w : w.charAt(0).toUpperCase() + w.slice(1)))
    .join('')
}
